import { Popover } from "@headlessui/react";
import { LockBodyScroll } from "cy-ui/components/LockBodyScroll";
import { cn } from "cy-fe/src/utils/cn";
import {
  ArrowLeftIcon,
  DollarSignIcon,
  LogOutIcon,
  SettingsIcon,
  UserIcon,
  UsersIcon,
} from "lucide-react";
import { signOut, useSession } from "next-auth/react";
import { NextLink, UnstyledButton } from "cy-fe";
import { useBreakpoint } from "cy-fe";
import { RoundButton, RoundIcon } from "cy-ui/components/RoundIcons";
import { PopoverTransition } from "cy-ui/components/transitions/PopoverTransition";
import { MenuItem } from "../../navigation/models";
import { urls } from "../../urls";

const menuItemColorCss =
  "text-gray-600 stroke-gray-500 group-hover:text-gray-800 group-hover:font-semibold group-hover:stroke-gray-800 ";
const defaultUserItems: MenuItem[] = [
  {
    name: "Your profile",
    action: urls.settings.profile.href,
    icon: UserIcon,
  },
  {
    name: "Limits",
    action: urls.settings.limits.href,
    icon: DollarSignIcon,
  },
  {
    name: "Settings",
    action: urls.settings.href,
    icon: SettingsIcon,
  },
  {
    name: "Sign out",
    action: () => signOut(),
    icon: LogOutIcon,
  },
];

export function UserProfileMenu({
  userNavigation = defaultUserItems,
  close,
}: {
  userNavigation?: MenuItem[];
  close: () => void;
}) {
  const session = useSession();
  const bp = useBreakpoint();
  const user = session.data?.user;
  return (
    <PopoverTransition>
      <Popover.Panel
        className={cn(
          "fixed z-50 max-sm:inset-0 sm:absolute sm:right-0 sm:mt-2.5 sm:origin-top-right",
          "bg-white p-8 ring-1 ring-gray-900/5 focus:outline-none sm:w-[27rem] sm:rounded-xl sm:p-6 sm:shadow-2xl",
        )}
      >
        {bp.breakpoint === "" && <LockBodyScroll />}
        <RoundButton
          className="sm:hidden"
          icon={ArrowLeftIcon}
          onClick={close}
          type="button"
        />

        <div className="flex flex-col items-center justify-center sm:contents">
          <RoundIcon className="mb-5 sm:hidden" icon={UsersIcon} />
          <h3 className="max-w-full overflow-x-clip text-center text-2xl leading-tight font-semibold text-ellipsis sm:text-lg">
            {user?.mobile}
          </h3>
          {/* Only show membership number for Shoprite customers (when externalId exists) */}
          {/* Tourvest customers don't have membership numbers (externalId is null) */}
          {user?.externalId && (
            <p className="mb-8 text-center text-sm text-gray-600">
              Membership number {user.externalId}
            </p>
          )}
        </div>
        <ul className="space-y-3">
          {userNavigation.map((item) => (
            <li
              className={cn(
                "group flex items-center gap-3 py-1 text-sm leading-6",
              )}
              key={item.name}
            >
              {typeof item.action === "string" ? (
                <NextLink
                  className="flex gap-2"
                  href={item.action}
                  key={item.name}
                  onClick={close}
                >
                  <item.icon className={menuItemColorCss} />
                  <p className={menuItemColorCss}>{item.name}</p>
                </NextLink>
              ) : (
                <UnstyledButton className="flex gap-2" onClick={item.action}>
                  <item.icon className={menuItemColorCss} />
                  <p className={menuItemColorCss}>{item.name}</p>
                </UnstyledButton>
              )}
            </li>
          ))}
        </ul>
      </Popover.Panel>
    </PopoverTransition>
  );
}
